import type {ChallengeResult, Sealed} from "../types";
import {doProofOfWork} from "./work";
import {debug, info, warn} from "../utils/log";
import {apiFetch} from "../utils/api";
import {API_PREFIX} from "../utils/constants";
import {getSignKeyPublic, rotateSignKey, signDataInline} from "../utils/crypto";
import {TranslatableError} from "../utils/error";
import {del, get, set} from "../utils/storage";

export async function getNewSolution(update: (rate: number) => void, signal: AbortSignal): Promise<string | undefined> {
    const start = Date.now();
    const previousWork = await get('lastWork');
    let lastChallenge: string | undefined = previousWork?.lastChallenge || undefined;
    let lastNonce: string | undefined = previousWork?.lastNonce || undefined;
    if (!previousWork) {
        // We need a new sign key to get a new challenge
        await rotateSignKey();
    }
    let publicSignKey = await getSignKeyPublic();
    const startOver = async () => {
        await rotateSignKey();
        publicSignKey = await getSignKeyPublic();
        lastChallenge = undefined;
        lastNonce = undefined;
        await del('lastWork')
    }
    try {
        let rate = NaN
        while (true) {
            const challenge: ChallengeResult = await apiFetch<ChallengeResult>(API_PREFIX + "pow/challenge", {
                body: {
                    publicSignKey,
                    lastChallenge,
                    lastNonce,
                },
                signal: signal,
            });
            if (challenge.rejected) {
                if (challenge.rejected === "key-reuse") {
                    warn(`Key reuse detected, rotating key and start over...`);
                    await startOver();
                    continue;
                }
                // noinspection ExceptionCaughtLocallyJS
                throw new TranslatableError({id: 'rejected'})
            }
            const t0 = Date.now();
            let lastSpeedUpdate = t0;
            let lastIter = 0;
            const {nonce, result} = await doProofOfWork(
                challenge,
                "0",
                (currentNonce) => {
                    const currentNonceNumber = Number(currentNonce);
                    const n = Date.now()
                    const delta = n - lastSpeedUpdate;
                    rate = delta === 0 ? 0 : (currentNonceNumber - lastIter) / delta;
                    update(rate)
                    lastSpeedUpdate = n;
                    lastIter = currentNonceNumber;
                },
                signal,
            );
            const elapsed = Date.now() - t0;
            const elapsedOverall = Date.now() - start;
            debug(`New solution computed: ${nonce} in ${elapsed} ms`, {
                challenge,
                nonce,
                result,
                elapsed,
                elapsedOverall,
            });
            lastChallenge = challenge.challenge;
            lastNonce = nonce.toString();
            const response = await apiFetch<Sealed>(API_PREFIX + "pow/submit", {
                body: {
                    challenge: challenge.challenge,
                    nonce: await signDataInline(nonce.toString()),
                    elapsed,
                    elapsedOverall,
                    publicSignKey,
                },
                signal: signal,
            });
            if (response.solution) {
                await del('lastWork')
                const solution = JSON.stringify(response.solution);
                info(`Solution computed and the server successfully attested it. This took ${elapsedOverall} ms from start to finish.`);
                return solution;
            } else {
                debug(`Server wants us to compute another solution, continuing...`);
                await set('lastWork', {
                    lastChallenge,
                    lastNonce,
                })
                update(rate) // inform the caller that we make progress
            }
        }
    } catch (err) {
        await startOver()
        throw err
    }
}
